import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { useTranslation } from "react-i18next";

const EditSubCategoryModal = ({
  isOpen,
  onClose,
  onSubmit,
  parentCategory,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  // ملء الحقول بالبيانات الحالية عند فتح الـ modal
  useEffect(() => {

    if (isOpen && parentCategory) {
       
      reset({
        translations: {
          ar: { name: parentCategory.translations?.ar?.name || "" },
          en: { name: parentCategory.translations?.en?.name || "" },
        },
        order: parentCategory.order || 0,
        image: null, // FileList فارغ للصورة الجديدة (الحالية تُعرض كـ preview)
        id: parentCategory.id, // ID للتحديث
      });
    }
  }, [isOpen, parentCategory, reset]);

  // Validation للصورة (اختيارية في التعديل)
  const imageValidation = {
    validate: (fileList) => {
      if (!fileList || fileList.length === 0) {
        return true; // اختيارية في التعديل
      }
      const file = fileList[0];
      if (file && !file.type.startsWith("image/")) {
        return t("invalidImageType"); // "نوع الملف غير مدعوم، اختر صورة"
      }
      if (file && file.size > 5 * 1024 * 1024) {
        // 5MB max
        return t("imageTooLarge"); // "حجم الصورة كبير جداً"
      }
      return true;
    },
  };

  const onFormSubmit = (data) => {
    // أضف ID الـ sub-category للتحديث
    const submitData = {
      ...data,
      id: parentCategory.id, // تأكيد ID
    };
    onSubmit(submitData); // يرسل البيانات إلى الـ parent
  };

  // Preview للصورة الحالية
  const currentImagePreview = parentCategory?.image ? (
    <div className="mt-2 p-2 bg-muted rounded-md border border-border">
      <p className="text-xs text-muted-foreground mb-1">{t("currentImage")}</p>
      <img
        src={parentCategory.image}
        alt={parentCategory.translations?.ar?.name || "Current"}
        className="w-16 h-16 object-cover rounded-md"
      />
      <p className="text-xs text-muted-foreground mt-1">
        {t("changeImageOptional")}
      </p>
    </div>
  ) : null;

  // Preview للصورة الجديدة إذا تم اختيارها
  const newImagePreview =
    watch("image") && watch("image").length > 0 ? (
      <div className="mt-2 p-2 bg-primary/10 rounded-md border border-primary/20">
        <p className="text-xs text-primary-foreground mb-1">
          {t("newImagePreview")}
        </p>
        <img
          src={URL.createObjectURL(watch("image")[0])}
          alt="New Preview"
          className="w-16 h-16 object-cover rounded-md"
        />
      </div>
    ) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
           تعديل فئة فرعية - {parentCategory?.translations?.ar?.name}
          </h3>
          <Button variant="ghost" onClick={onClose} size="icon">
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* عرض الـ Parent Category (الفئة الأم للـ sub-category) - إذا كان معروفاً */}
        {parentCategory &&
          parentCategory.parent && ( // افترض أن parentCategory.parent موجود في البيانات
            <div className="mb-4 p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                {t("parentCategory")}:{" "}
                {parentCategory.parent.translations?.ar?.name}
              </p>
            </div>
          )}

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="space-y-4">
            {/* حقل الاسم العربي - مملوء بالحالي */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("arabicSubCategoryName")} *
              </label>
              <input
                {...register("translations.ar.name", {
                  required: t("nameRequired"),
                })}
                className="w-full p-2 border border-border rounded-md"
                placeholder={t("arNameError")}
              />
              {errors.translations?.ar?.name && (
                <p className="text-sm text-destructive mt-1">
                  {errors.translations.ar.name.message}
                </p>
              )}
            </div>

      

            {/* حقل الصورة - اختيارية، مع preview للحالية */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("categoryImage")} 
              </label>
              <input
                type="file"
                {...register("image", imageValidation)}
                className="w-full p-2 border border-border rounded-md file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                accept="image/*"
              />
              {errors.image && (
                <p className="text-sm text-destructive mt-1">
                  {errors.image.message}
                </p>
              )}
              {currentImagePreview}
              {newImagePreview}
            </div>

            {/* حقل Order - مملوء بالحالي */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("order")}
              </label>
              <input
                type="number"
                {...register("order", { valueAsNumber: true, min: 0 })}
                className="w-full p-2 border border-border rounded-md"
                placeholder={t("enterOrder")}
                min="0"
              />
              {errors.order && (
                <p className="text-sm text-destructive mt-1">
                  {errors.order.message}
                </p>
              )}
            </div>

            {/* حقل مخفي للـ ID (للتحديث) */}
            <input
              type="hidden"
              value={parentCategory?.id || ""}
              {...register("id")}
            />
          </div>

          <div className="flex justify-end space-x-2 mt-6 gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button type="submit">{t("saveChanges")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubCategoryModal;
